import { useCallback, useMemo, useRef, useState } from 'react'

interface PaginationResult<T> {
    currentPage: number
    pageSize: number
    totalPages: number | null
    paginatedData: T[]
    cumulativeData: T[]
    totalItems: number | null
    loading: boolean
    error: string | null

    goToPage: (page: number) => void
    nextPage: () => void
    previousPage: () => void
    goToFirstPage: () => void
    goToLastPage: () => void

    hasNextPage: boolean
    hasPreviousPage: boolean

    reset: () => void
}

interface FetchPageResult<T> {
    items: T[]
    totalItems: number
}

/*
    this hook can be used to get data from paginated api

    How this hooks work
    - it will call the first page when reset will be called
    - whenever you want to clear the data and fetch data from start -> just reset

*/
function useLazyPagination<T>(
    fetchPage: (page: number) => Promise<FetchPageResult<T>>,
    pageSize = 10,
): PaginationResult<T> {
    const [currentPage, setCurrentPage] = useState(1)
    const [totalItems, setTotalItems] = useState<number | null>(null)
    const [loadedPages, setLoadedPages] = useState<Record<number, T[]>>({})
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // This ref tracks which pages have been loaded to avoid putting loadedPages in the useEffect deps
    const loadedPageNumbersRef = useRef<Set<number>>(new Set())

    const totalPages = totalItems === null ? null : Math.ceil(totalItems / pageSize)

    const paginatedData = useMemo(() => {
        return loadedPages[currentPage] || []
    }, [loadedPages, currentPage])

    const cumulativeData = useMemo(() => {
        return Object.keys(loadedPages)
            .map(Number)
            .sort((a, b) => a - b)
            .flatMap(page => loadedPages[page])
    }, [loadedPages])

    const fetchAndStorePage = useCallback(
        async (page: number) => {
            if (loadedPageNumbersRef.current.has(page)) {
                return
            }

            setLoading(true)
            setError(null)

            try {
                const result = await fetchPage(page)
                setLoadedPages(prev => ({ ...prev, [page]: result.items }))
                setTotalItems(result.totalItems)

                loadedPageNumbersRef.current.add(page)
            } catch (error_) {
                setError((error_ as Error).message || 'Failed to load data')
            } finally {
                setLoading(false)
            }
        },
        [fetchPage],
    )

    const goToPage = (page: number): void => {
        if (totalPages !== null) {
            page = Math.max(1, Math.min(page, totalPages))
        }
        setCurrentPage(page)
        fetchAndStorePage(page)
    }

    const reset = useCallback(() => {
        setCurrentPage(1)
        setTotalItems(null)
        setLoadedPages({})
        setError(null)
        loadedPageNumbersRef.current = new Set()
        fetchAndStorePage(1)
    }, [fetchAndStorePage])

    return {
        currentPage,
        pageSize,
        totalPages,
        paginatedData,
        cumulativeData,
        totalItems,

        goToPage,
        nextPage: () => goToPage(currentPage + 1),
        previousPage: () => goToPage(currentPage - 1),
        goToFirstPage: () => goToPage(1),
        goToLastPage: () => {
            if (totalPages) {
                goToPage(totalPages)
            }
        },

        hasNextPage: totalPages !== null && currentPage < totalPages,
        hasPreviousPage: currentPage > 1,

        loading,
        error,

        reset,
    }
}

export default useLazyPagination
